import '../App.css';
import backgroundImage from "../public/Bildergruppen.png";
import tabletBackgroundImage from "../public/BildergruppenTablet.png";
import phoneBackground from "../public/phoneBackgroundOpacity.png";
import { useCallback, useRef, useState } from 'react';
import CookieConsent, { acceptMarketingCookiesForFormSubmission } from '../CookieConsent';
import { DatenschutzInfoPopover } from './DatenschutzInfoPopover';
import { TeilnahmebedingungenModal } from './TeilnahmebedingungenModal';
import { submitPromoLead, trackEmailSignup, trackStudioLeadComplete } from '../tracking';
import Modal from './Modal';
import Funktioniert from './Funktioniert';
import ImagesCarousel from './ImagesCarousel';
import BecomeHost from './BecomeHost';
import BottomFooter from './Footer/BottomFooter';
import rocket from "../public/rocketImage.png";

function Home() {
  const emailInputRef = useRef(null);
  const emailSignupAnchorRef = useRef(null);
  const [email, setEmail] = useState('');
  const [emailState, setEmailState] = useState('default'); // 'default', 'empty', 'valid', 'awaitingStudio', 'success'
  const [showSuccess, setShowSuccess] = useState(false);
  const [isChecked, setIsChecked] = useState(false);
  const [showTeilnahmeModal, setShowTeilnahmeModal] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showFormSuccess, setShowFormSuccess] = useState(false);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitError('');
    if (!email.trim()) {
      setEmailState('empty');
      return;
    }
    if (!validateEmail(email)) {
      setEmailState('empty');
      return;
    }
    setEmailState('valid');
    acceptMarketingCookiesForFormSubmission();

    if (isChecked) {
      setEmailState('awaitingStudio');
      setShowForm(true);
      return;
    }

    setSubmitting(true);
    try {
      await submitPromoLead({
        email: email.trim(),
        agbAccepted: true,
        isStudio: false,
      });
      setEmailState('success');
      setShowSuccess(true);
      trackEmailSignup(email, false);
    } catch (err) {
      setSubmitError(err?.message || 'Die Anmeldung konnte nicht gespeichert werden.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    acceptMarketingCookiesForFormSubmission();
    await submitPromoLead({
      email: (formData.email || email).trim(),
      agbAccepted: true,
      isStudio: true,
      studioName: formData.studioname?.trim(),
      phoneNumber: formData.telefon?.trim(),
    });
    trackStudioLeadComplete();
    setShowSuccess(true);
    setEmailState('success');
    setShowFormSuccess(true);
  };

  const handleModalClose = () => {
    const completed = showFormSuccess;
    setShowForm(false);
    setShowFormSuccess(false);
    setEmailState((prev) =>
      prev === 'awaitingStudio' ? (validateEmail(email) ? 'valid' : 'default') : prev
    );
    if (!completed) {
      setIsChecked(false);
    }
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    
    if (showSuccess || emailState === 'awaitingStudio') {
      setShowSuccess(false);
      setEmailState('default');
      setSubmitError('');
    } else if (value.trim()) {
      if (validateEmail(value)) {
        setEmailState('valid');
      } else {
        setEmailState('default');
      }
    } else {
      setEmailState('default');
    }
  };

  const handleCheckboxToggle = (e) => {
    e.preventDefault();
    const next = !isChecked;
    if (next) {
      setIsChecked(true);
      setShowForm(true);
      return;
    }
    setIsChecked(false);
    setShowForm(false);
    setShowFormSuccess(false);
    setEmailState((prev) =>
      prev === 'awaitingStudio' ? (validateEmail(email) ? 'valid' : 'default') : prev
    );
  };

  const awaitingStudio = emailState === 'awaitingStudio';

  const focusHeroEmail = useCallback(() => {
    emailSignupAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    window.setTimeout(() => {
      emailInputRef.current?.focus({ preventScroll: true });
    }, 320);
  }, []);

  const openStudioHostModal = useCallback(() => {
    setIsChecked(true);
    setShowForm(true);
  }, []);

  return (
    <div className="homeContainer">
      <div className="container">
        <div className="backgroundImageContainer">
          <img src={backgroundImage} alt="Background Studios" />
        </div>
        <div className="tabletBackgroundImageContainer">
          <img src={tabletBackgroundImage} alt="Background Studios" />
        </div>
        <div className="phoneBackgroundImageContainer">
          <img src={phoneBackground} alt="Background Studios" />
        </div>
        <header className="header">
          <svg xmlns="http://www.w3.org/2000/svg" width="64" height="22" viewBox="0 0 64 22" fill="none">
            <g clipPath="url(#clip0_14975_105551)">
            <path d="M11.5921 11.3519L9.93587 11.2128C5.83781 10.8811 4.45763 10.4531 4.45763 9.52225C4.45763 8.59139 6.19878 8.07781 9.78724 8.07781C13.5774 8.07781 15.1062 8.58069 15.4247 9.92884L15.4778 10.1428H19.6396L19.5971 9.84324C19.183 6.29099 16.0193 4.56836 9.93587 4.56836C3.85248 4.56836 0.731158 6.25889 0.731158 9.57575C0.731158 12.6144 3.22609 14.0803 9.36257 14.6366L10.8489 14.7757C14.9151 15.1288 16.4227 15.6638 16.4227 16.7338C16.4227 17.9 14.5648 18.4992 10.8914 18.4992C6.72961 18.4992 4.60627 17.7181 4.40455 16.1025L4.3727 15.8671H0.210938V16.156C0.402039 20.0399 3.88433 22.0086 10.5729 22.0086C17.2614 22.0086 20.1598 20.1683 20.1598 16.5412C20.1598 13.3099 17.7604 11.8548 11.5921 11.3519Z" fill="white"/>
            <path d="M50.9682 5.10271V12.036C50.9682 15.92 48.4308 18.3274 44.354 18.3274C40.5213 18.3274 39.0243 16.6047 39.0243 12.1751V5.10271H26.1569V-0.000976562L22.3242 1.62536V16.2409C22.3242 20.2747 24.2565 21.9973 28.7898 21.9973C30.3717 21.9973 31.8899 21.7298 33.1745 21.4623L33.3869 21.4195V17.985L33.0684 18.0492C31.4015 18.3916 30.6584 18.52 29.4693 18.52C27.2716 18.52 26.1569 17.9957 26.1569 15.1175V8.31258H37.0921L35.0749 9.85331V13.427C35.0749 19.2796 37.538 21.9973 42.8145 21.9973C46.2225 21.9973 49.0253 20.8097 50.9682 18.5414V21.4623H54.9176V5.10271H50.9682Z" fill="white"/>
            <path d="M60.448 9.71786C61.8819 9.71786 63.0444 8.59812 63.0444 7.21684C63.0444 5.83557 61.8819 4.71582 60.448 4.71582C59.014 4.71582 57.8516 5.83557 57.8516 7.21684C57.8516 8.59812 59.014 9.71786 60.448 9.71786Z" fill="#FF003E"/>
            </g>
            <defs>
            <clipPath id="clip0_14975_105551">
            <rect width="62.8828" height="22.009" fill="white" transform="translate(0.210938)"/>
            </clipPath>
            </defs>
          </svg>
          <a href="https://stu-studios.de/info/" target="_blank" rel="noreferrer">So funktioniert Stu</a>
        </header>
          <div className='content'>
            <div className='titleContainer'>
              <h1 className='titleContainerDesktop'>Finde Studios & Kreative</h1>
              <h1 className='titleContainerPhone'>Studios & Kreative <br/>
              für deine Vision.</h1>
              <h3>Sichere dir jetzt 25 € auf deine erste Buchung. </h3>
              <div className='spamContainer'>
                <div className='rocketImageContainer'>
                  <img src={rocket} alt="Rocket" />
                </div>
                <span>Wir launchen bald</span>
              </div>
            </div>
            <div ref={emailSignupAnchorRef} id="hero-email-signup" className="inputsContainer">
              <div className={`emailInputContainer ${emailState === 'empty' ? 'error' : ''} ${emailState === 'success' ? 'success' : ''}`}>
                {!showSuccess ? (
                  <>
                    <input 
                      ref={emailInputRef}
                      className={`emailInput ${emailState === 'empty' ? 'error' : ''}`}
                      type="email" 
                      placeholder="E-Mail eingeben" 
                      value={email}
                      onChange={handleEmailChange}
                      disabled={awaitingStudio}
                    />
                    <button 
                      className={`joinButton ${emailState === 'valid' || awaitingStudio ? 'valid' : ''}`}
                      onClick={handleSubmit}
                      disabled={awaitingStudio || submitting}
                    >
                      {submitting ? 'Wird gesendet…' : 'Beitreten'}
                    </button>
                  </>
                ) : (
                  <div className='successMessage'>
                    <span>Danke! Dein 25€ Vorteil ist reserviert.</span>
                  </div>
                )}
              </div>
              {!showSuccess && (
                <>
                <div className="promoConsentDisclaimer">
                  Mit Klick auf Beitreten akzeptierst du die{' '}
                  <button
                    type="button"
                    className="promoLegalLink"
                    onClick={() => setShowTeilnahmeModal(true)}
                  >
                    Teilnahmebedingungen
                  </button>{' '}
                  und die{' '}
                  <DatenschutzInfoPopover
                    triggerClassName="promoLegalLink datenschutzLink"
                    placement="below"
                    triggerText="Datenschutzinformationen"
                  />{' '}
                  zur 25€-Aktion.
                </div>
                <span className='spamNote'>Kein Spam. Versprochen.</span>
                </>
              )}
              {!showSuccess && (
                <div onClick={handleCheckboxToggle} className='checkBoxContainer'>
                  <div className='switchContainer'>
                        <label className='switch'>
                            <input 
                                type="checkbox" 
                                checked={isChecked}
                                onClick={handleCheckboxToggle}
                            />
                            <span className='slider'></span>
                        </label>
                    </div>
                  <span>Ich habe ein Studio</span>
                </div>
              )}
              {submitError && !showSuccess && <p className="promoSubmitError">{submitError}</p>}
              {/* {!showSuccess && (
                <p className="promoHintText">
                  Der 25€ Vorteil wird automatisch bei deiner ersten Buchung in der Su-App berucksichtigt,
                  sofern diese mindestens 25,50€ betragt. Keine Barauszahlung, nicht ubertragbar.
                </p>
              )} */}
            </div>
          </div>
          <div className='spamContainerPhone'>
            <span>Kein Spam. Versprochen.</span>
          </div>
        <Modal 
          isOpen={showForm}
          onClose={handleModalClose}
          showFormSuccess={showFormSuccess}
          onFormSubmit={handleFormSubmit}
        />
        <TeilnahmebedingungenModal
          open={showTeilnahmeModal}
          onClose={() => setShowTeilnahmeModal(false)}
        />
        <CookieConsent />
      </div>
      <Funktioniert onJoinDiscountClick={focusHeroEmail} />
      <ImagesCarousel />
      <BecomeHost onGastgeberClick={openStudioHostModal} />
      <BottomFooter />
    </div>
  );
}

export default Home;

